import {
  MRDCell,
  MRDMultiSelectEditor,
  RefDataSourceIdsCell,
  suppressDropdownKeyboardEvent,
} from '@/components/Grid'
import { fetchClient } from '@api/typedClient'
import {
  CoreCrossroadsMapping,
  CrossroadsMapping,
  MAPPING_ENTITY_MAP,
} from '@api/types'
import {
  GetDetailRowDataParams,
  GetRowIdParams,
  ICellRendererParams,
  IDetailCellRendererParams,
  NewValueParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import React, { useCallback, useMemo } from 'react'
import { DiagnoseTankCell } from '../components/DiagnoseTankCell'

const formatTankNumber = (params: ValueFormatterParams): string => {
  const value = params.value
  if (value == null) return ''
  return Number(value).toLocaleString()
}

export function useChildMappingGridParams(editable: boolean = true) {
  const forcedSearchTerm = useCallback(
    (
      mgParams: IDetailCellRendererParams<
        CrossroadsMapping,
        CoreCrossroadsMapping
      >,
    ) => {
      if (mgParams.data?.goids && mgParams.data!.goids.length > 0) {
        return mgParams.data!.goids[0]
      }
      return '$show_no_results'
    },
    [],
  )

  const onChildCellValueChanged = useCallback(
    async (
      params: NewValueParams<CoreCrossroadsMapping, unknown>,
      mgParams: IDetailCellRendererParams<
        CrossroadsMapping,
        CoreCrossroadsMapping
      >,
    ) => {
      const newChildMapping = params.data
      const field = params.colDef.field!.toString()
      const oldChildMapping = {
        ...newChildMapping,
        [field]: params.oldValue,
      }
      const childSourceId = newChildMapping.source_id
      const parentMappingId = mgParams.data!._id

      try {
        const { data, error } = await fetchClient.PUT(
          // @ts-expect-error TODO: Add /crossroads/mappings/{mapping_id}/child/{source_id} to OpenAPI spec
          `/crossroads/mappings/${parentMappingId}/child/${childSourceId}`,
          { body: newChildMapping },
        )
        if (error) {
          throw new Error(`Failed to update mapping: ${JSON.stringify(error)}`)
        }
        const response = data as unknown as CrossroadsMapping
        params.node!.setData(response.children[childSourceId])
        // Update parent row data so collapse/re-expand shows updated values
        mgParams.node.setData(response)
      } catch (error) {
        console.error('Failed to update mapping:', error)
        // Revert the value on error
        params.node!.setData(oldChildMapping)
        throw error
      }
    },
    [],
  )

  return useMemo(() => {
    return (
      mgParams: IDetailCellRendererParams<
        CrossroadsMapping,
        CoreCrossroadsMapping
      >,
    ) => {
      return {
        detailGridOptions: {
          context: {
            id: mgParams.data?._id,
            mgparams: mgParams,
          },
          getRowId: (params: GetRowIdParams<CoreCrossroadsMapping>) =>
            `${mgParams.data?._id}+${params.data?.source_id}`,
          columnDefs: [
            { field: 'display_name', headerName: 'Name', width: 200 },
            { field: 'source_id', headerName: 'Source ID', width: 150 },
            { field: 'type', headerName: 'Type', width: 100 },
            {
              field: 'goids',
              headerName: 'Mapped To',
              editable: editable,
              width: 300,
              suppressKeyboardEvent: suppressDropdownKeyboardEvent,
              cellRenderer: (
                params: ICellRendererParams<CoreCrossroadsMapping>,
              ) => {
                const { data } = params
                return (
                  <MRDCell
                    goids={data?.goids || []}
                    objectType={data?.type || ''}
                    status={data?.status ?? 'unused'}
                    maxVisible={1}
                  />
                )
              },
              cellEditor: MRDMultiSelectEditor,
              cellEditorPopup: true,
              cellEditorParams: (params) => {
                return {
                  type: MAPPING_ENTITY_MAP[params.data.type],
                  forcedSearchTerm: forcedSearchTerm(mgParams),
                  multi: false,
                }
              },
              valueSetter: (params) => {
                params.data.goids =
                  params.newValue != null ? [params.newValue] : []
                return true
              },
              onCellValueChanged: (params) =>
                onChildCellValueChanged(params, mgParams),
            },
            {
              field: 'product_id',
              headerName: 'Product',
              width: 300,
              cellRenderer: (
                params: ICellRendererParams<CoreCrossroadsMapping>,
              ) => {
                // biome-ignore lint/suspicious/noExplicitAny: product_id added dynamically to CoreCrossroadsMapping
                const productId = (params.data as any)?.product_id
                if (!productId) return null
                return (
                  <RefDataSourceIdsCell
                    sourceIds={[productId]}
                    sourceSystem={mgParams.data?.source_system}
                    mappingType='product'
                    popoverTitle='Products'
                    maxVisible={1}
                  />
                )
              },
            },
            {
              field: 'tank_size',
              headerName: 'Tank Size',
              width: 130,
              valueFormatter: formatTankNumber,
            },
            {
              field: 'storage_max',
              headerName: 'Storage Max',
              width: 130,
              valueFormatter: formatTankNumber,
            },
            {
              field: 'fuel_bottom',
              headerName: 'Fuel Bottom',
              width: 130,
              valueFormatter: formatTankNumber,
            },
            {
              colId: 'diagnose',
              headerName: '',
              width: 60,
              sortable: false,
              filter: false,
              pinned: 'right',
              cellRenderer: (
                params: ICellRendererParams<CoreCrossroadsMapping>,
              ) => (
                <DiagnoseTankCell
                  isTank={params.data?.type === 'tank'}
                  siteSourceId={mgParams.data?.source_id}
                  tankId={params.data?.source_id}
                  sourceSystem={mgParams.data?.source_system}
                />
              ),
            },
          ],
          defaultColDef: { resizable: true },
        },
        getDetailRowData: (
          params: GetDetailRowDataParams<
            CrossroadsMapping,
            CoreCrossroadsMapping
          >,
        ) => {
          // Children have moved from `children` to `details.tanks` for site mappings
          const mapping = params.data as CrossroadsMapping & {
            details?: { type?: string; tanks?: Record<string, unknown> }
          }

          let childData: CoreCrossroadsMapping[] = []

          // Check new structure: details.tanks for site mappings
          if (mapping.details?.type === 'site' && mapping.details?.tanks) {
            childData = Object.values(mapping.details.tanks).map((tank) => {
              const t = tank as {
                tank_id?: string
                display_name?: string
                goids?: string[]
                source_id?: string
                type?: string
                product_id: string
                tank_size?: number | null
                storage_max?: number | null
                fuel_bottom?: number | null
              }
              return {
                type: 'tank',
                display_name: t.display_name || t.tank_id || '',
                source_id: t.source_id || t.tank_id || '',
                goids: t.goids || [],
                matching_info: {},
                extra_data: {},
                status: (t.goids?.length ?? 0) > 0 ? 'mapped' : 'needs mapping',
                product_id: t.product_id || '',
                tank_size: t.tank_size ?? null,
                storage_max: t.storage_max ?? null,
                fuel_bottom: t.fuel_bottom ?? null,
              } as CoreCrossroadsMapping
            })
          }
          // Fall back to old structure for backwards compatibility
          else if (mapping.children) {
            childData = Object.values(mapping.children)
          }

          params.successCallback(childData)
        },
      }
    }
  }, [])
}
